import React from 'react';
import Scrollbars from 'react-custom-scrollbars';

import { Users, StarOutline } from '@crehana/icons';

import { Avatar, LazyImage, colors } from '@crehana/ui';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { Presenter } from 'Jsx/CrehanaLive/graphql/types/Presenter';
import { LiveNode } from 'Jsx/CrehanaLive/graphql/types/LiveNode';
import VideoInfo from 'Jsx/CrehanaLive/components/VideoInfo';

interface PresenterRowProps extends Presenter {
  displaySeparator: boolean;
}

const PresenterRow: React.FC<PresenterRowProps> = ({
  displaySeparator,
  bio,
  fullName,
  occupation,
  profileUrl,
  thumbnailUrl,
}) => {
  return (
    <React.Fragment>
      <div className="flex items-start">
        <a
          className="inline-block"
          href={profileUrl!}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Avatar src={thumbnailUrl} alt={fullName || undefined} size={40} />
        </a>
        <a
          className="inline-block ml-16"
          href={profileUrl!}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="font-subtitle4 text-white block">{fullName}</span>
          <span
            className="font-caption text-base-lighter block bg-transparent"
            style={{ marginTop: 2 }}
          >
            {occupation}
          </span>
        </a>
      </div>
      <p className="font-body4 text-gray-dark mt-8">{bio}</p>
      {displaySeparator && (
        <div className="my-32 border-b border-solid border-gray-dark" />
      )}
    </React.Fragment>
  );
};

interface InformationProps {
  live: LiveNode;
  isFuture?: boolean;
  enrollToLive?: (liveId: number) => Promise<void>;
  isForbiddenPremiumLive?: boolean;
}

const Information: React.FC<InformationProps> = ({
  live,
  enrollToLive,
  isFuture,
  isForbiddenPremiumLive,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const presenters = React.useMemo(() => {
    if (live.presenters?.edges.length) {
      return live.presenters.edges.map(e => e!.node!);
    }
    if (live.presenter) {
      return [live.presenter];
    }
    return [];
  }, [live.presenter, live.presenters]);
  const totalPresenters = presenters.length;

  return (
    <Scrollbars className="h-full">
      <div className="px-32 py-24">
        <div className="block mb-24 md:hidden">
          <VideoInfo
            isFuture={isFuture}
            enrollToLive={enrollToLive}
            isForbiddenPremiumLive={isForbiddenPremiumLive}
            {...live}
          />
        </div>
        <p className="font-label text-base-lighter">
          {t('DESCRIPTION_LIVE_LABEL')}
        </p>
        {live.fullDescription && (
          <p
            className="font-body4 text-gray-dark mt-16"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: live.fullDescription.replace(/(?:\r\n|\r|\n)/g, '<br />'),
            }}
          />
        )}
        {totalPresenters ? (
          <div className="mt-40">
            <p className="font-label text-base-lighter mb-16">
              {t('ABOUT_SPEAKER_LABEL')}
            </p>
            {presenters.map((presenter, i) => (
              <PresenterRow
                key={presenter.originalId!}
                displaySeparator={
                  totalPresenters > 1 && totalPresenters - 1 !== i
                }
                {...presenter}
              />
            ))}
          </div>
        ) : null}
        {live.coursePreSale && live.coursePreSale.url && (
          <div className="mt-40">
            <span className="font-label text-base-lighter">
              {t('SIGN_UP_LIVE_COURSE_LABEL')}
            </span>
            <div className="flex flex-row flex-no-wrap items-start mt-16">
              <a
                className="inline-block"
                href={live.coursePreSale.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LazyImage
                  src={live.coursePreSale.promoImage!}
                  alt={live.coursePreSale.title}
                  placeholder={live.coursePreSale.imagePlaceholder}
                  style={{
                    height: 88,
                    width: 88,
                  }}
                  imgxParams={{
                    w: 88,
                    h: 88,
                  }}
                  isDark
                />
              </a>
              <div
                className="flex flex-col justify-between w-full ml-8"
                style={{ height: 88 }}
              >
                <a
                  className="font-subtitle3 text-white"
                  href={live.coursePreSale.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {live.coursePreSale.title}
                </a>
                <div className="flex items-center">
                  <span className="inline-block h-20">
                    <Users size={16} color={colors.grayDark} />
                  </span>
                  <span className="font-body4 text-gray-dark inline-block ml-8 mr-16">
                    {live.coursePreSale.enrollments}
                  </span>

                  {live.coursePreSale.score?.average &&
                    live.coursePreSale.score.average > 0 && (
                      <React.Fragment>
                        <span className="inline-block h-20">
                          <StarOutline size={16} color={colors.grayDark} />
                        </span>
                        <span className="font-body4 text-gray-dark inline-block ml-8">
                          {live.coursePreSale.score.average}
                        </span>
                      </React.Fragment>
                    )}
                </div>
              </div>
            </div>
            <a
              className="font-link text-secondary-light block mt-20"
              href={live.coursePreSale.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('MORE_COURSE_INFO_LIVE_LABEL')}
            </a>
          </div>
        )}
      </div>
    </Scrollbars>
  );
};

export default React.memo(Information);
